import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_ImageFilters() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🎨 Advanced CSS: Image Filters</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS <code>filter</code> property allows you to apply visual effects to
        images without editing them in external software. You can blur, change
        brightness, grayscale, hue, contrast, and combine multiple effects.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Use <code>filter</code> to enhance images directly in CSS.
          </li>
          <li>
            Common filters: <code>blur()</code>, <code>grayscale()</code>,{" "}
            <code>brightness()</code>, <code>contrast()</code>,{" "}
            <code>hue-rotate()</code>.
          </li>
          <li>Combine multiple filters in one line for advanced effects.</li>
          <li>
            Transitions make filter effects smooth on hover or state changes.
          </li>
          <li>
            Filters can also be applied to <code>video</code> or{" "}
            <code>canvas</code> elements.
          </li>
        </ul>
      </div>

      {/* Example 1: Blur */}
      <div className="tutorial-section">
        <h2 className="section-title">✨ Blur</h2>
        <CodeCopyBlock
          code={`img {
  filter: blur(5px);
}`}
        />
        <img
          src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=300&q=80"
          alt="Blur example"
          style={{
            filter: "blur(5px)",
            marginTop: "1rem",
            width: "250px",
            borderRadius: "12px",
          }}
        />
      </div>

      {/* Example 2: Grayscale */}
      <div className="tutorial-section">
        <h2 className="section-title">🖤 Grayscale</h2>
        <CodeCopyBlock
          code={`img {
  filter: grayscale(80%);
}`}
        />
        <img
          src="https://images.unsplash.com/photo-1518791841217-8f162f1e1131?auto=format&fit=crop&w=300&q=80"
          alt="Grayscale example"
          style={{
            filter: "grayscale(80%)",
            marginTop: "1rem",
            width: "250px",
            borderRadius: "12px",
          }}
        />
      </div>

      {/* Example 3: Brightness */}
      <div className="tutorial-section">
        <h2 className="section-title">💡 Brightness</h2>
        <CodeCopyBlock
          code={`img {
  filter: brightness(150%);
}`}
        />
        <img
          src="https://images.unsplash.com/photo-1526772662000-3f88f10405ff?auto=format&fit=crop&w=300&q=80"
          alt="Brightness example"
          style={{
            filter: "brightness(150%)",
            marginTop: "1rem",
            width: "250px",
            borderRadius: "12px",
          }}
        />
      </div>

      {/* Example 4: Contrast */}
      <div className="tutorial-section">
        <h2 className="section-title">🎨 Contrast</h2>
        <CodeCopyBlock
          code={`img {
  filter: contrast(200%);
}`}
        />
        <img
          src="https://images.unsplash.com/photo-1519125323398-675f0ddb6308?auto=format&fit=crop&w=300&q=80"
          alt="Contrast example"
          style={{
            filter: "contrast(200%)",
            marginTop: "1rem",
            width: "250px",
            borderRadius: "12px",
          }}
        />
      </div>

      {/* Example 5: Hue Rotate */}
      <div className="tutorial-section">
        <h2 className="section-title">🌈 Hue Rotate</h2>
        <CodeCopyBlock
          code={`img {
  filter: hue-rotate(90deg);
}`}
        />
        <img
          src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=300&q=80"
          alt="Hue Rotate example"
          style={{
            filter: "hue-rotate(90deg)",
            marginTop: "1rem",
            width: "250px",
            borderRadius: "12px",
          }}
        />
      </div>

      {/* Example 6: Combined Filters */}
      <div className="tutorial-section">
        <h2 className="section-title">⚡ Combined Filters</h2>
        <CodeCopyBlock
          code={`img {
  filter: grayscale(50%) blur(2px) brightness(120%) contrast(150%);
}`}
        />
        <img
          src="https://images.unsplash.com/photo-1518791841217-8f162f1e1131?auto=format&fit=crop&w=300&q=80"
          alt="Combined filters example"
          style={{
            filter: "grayscale(50%) blur(2px) brightness(120%) contrast(150%)",
            marginTop: "1rem",
            width: "250px",
            borderRadius: "12px",
          }}
        />
      </div>

      {/* Hover + Transition Example */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Hover Effects</h2>
        <CodeCopyBlock
          code={`img {
  transition: filter 0.5s ease;
}

img:hover {
  filter: grayscale(0%) blur(0px) brightness(100%);
}`}
        />
        <img
          src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=300&q=80"
          alt="Hover filter example"
          style={{
            filter: "grayscale(100%) blur(3px) brightness(80%)",
            transition: "filter 0.5s ease",
            marginTop: "1rem",
            width: "250px",
            borderRadius: "12px",
          }}
        />
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>Combine CSS filters with transitions for interactive effects.</li>
          <li>Experiment with filters on background images or videos.</li>
          <li>Use CSS variables to dynamically change filter values.</li>
          <li>
            Apply filters conditionally with media queries or hover states.
          </li>
          <li>
            Combine filters with 2D/3D transforms for advanced image effects.
          </li>
        </ul>
      </div>
    </div>
  );
}
